const logLevel = {
  TRACE: {
    value: 0,
    name: 'TRACE'
  },
  DEBUG: {
    value: 1,
    name: 'DEBUG'
  },
  INFO: {
    value: 2,
    name: 'INFO'
  },
  WARN: {
    value: 3,
    name: 'WARN'
  },
  ERROR: {
    value: 4,
    name: 'ERROR'
  },
  FATAL: {
    value: 5,
    name: 'FATAL'
  },
  OFF: {
    value: 6,
    name: 'OFF'
  }
};
const defaultConfig = {
  level: logLevel.INFO,
  dateFormat: 'yyyy-MM-dd HH:mm:ss.SSS',
  showColors: true,
  appenders: ['console']
};
const levelColors = {
  [logLevel.TRACE.name]: '#6C757D',
  // gray
  [logLevel.DEBUG.name]: '#17A2B8',
  // cyan
  [logLevel.INFO.name]: '#28A745',
  // green
  [logLevel.WARN.name]: '#FFC107',
  // yellow
  [logLevel.ERROR.name]: '#DC3545',
  // red
  [logLevel.FATAL.name]: '#7E0537' // dark purple
};
let globalConfig = {
  ...defaultConfig
};
const formatDate = (date, format) => {
  const pad = function (num) {
    let size = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;
    return num.toString().padStart(size, '0');
  };
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const milliseconds = pad(date.getMilliseconds(), 3);
  return format.replace('yyyy', year).replace('MM', month).replace('dd', day).replace('HH', hours).replace('mm', minutes).replace('ss', seconds).replace('SSS', milliseconds);
};
const formatLogMessage = (loggerName, level, message) => {
  const timestamp = formatDate(new Date(), globalConfig.dateFormat);
  return {
    timestamp,
    level: level.name,
    prefix: loggerName,
    message
  };
};
const consoleAppender = logData => {
  const {
    timestamp,
    level,
    prefix,
    message
  } = logData;
  const logPrefix = `[${timestamp}] [${level}] ${prefix}: `;
  if (Array.isArray(message)) {
    const [firstArg, ...restArgs] = message;
    if (globalConfig.showColors) {
      // eslint-disable-next-line no-console
      console.log(`%c${logPrefix}${firstArg}`, `color: ${levelColors[logData.level]}`, ...restArgs);
    } else {
      // eslint-disable-next-line no-console
      console.log(logPrefix + firstArg, ...restArgs);
    }
  } else if (globalConfig.showColors) {
    // eslint-disable-next-line no-console
    console.log(`%c${logPrefix}`, `color: ${levelColors[logData.level]}`, message);
  } else {
    // eslint-disable-next-line no-console
    console.log(logPrefix, message);
  }
};
const isDebugFlagEnabled = function () {
  let debugParam = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'debugWallet';
  if (typeof window === 'undefined') return false;
  const url = new URL(window.location.href);
  return url.searchParams.get(debugParam) === 'true';
};
const logMessage = function (loggerName, level) {
  if (level.value < globalConfig.level.value) {
    return;
  }

  // Check if the log level is DEBUG or TRACE and if the debug flag is enabled
  if ((level === logLevel.DEBUG || level === logLevel.TRACE) && !isDebugFlagEnabled()) {
    return;
  }
  for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    args[_key - 2] = arguments[_key];
  }
  const logData = formatLogMessage(loggerName, level, args);
  if (globalConfig.appenders.includes('console')) {
    consoleAppender(logData);
  }
};
const configure = function () {
  let config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  globalConfig = {
    ...globalConfig,
    ...config,
    level: config.level || globalConfig.level,
    dateFormat: config.dateFormat || globalConfig.dateFormat,
    appenders: config.appenders || globalConfig.appenders
  };
};
const getLogger = function () {
  let name = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return {
    trace: function () {
      for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }
      return logMessage(name, logLevel.TRACE, ...args);
    },
    debug: function () {
      for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
        args[_key3] = arguments[_key3];
      }
      return logMessage(name, logLevel.DEBUG, ...args);
    },
    info: function () {
      for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
        args[_key4] = arguments[_key4];
      }
      return logMessage(name, logLevel.INFO, ...args);
    },
    warn: function () {
      for (var _len5 = arguments.length, args = new Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
        args[_key5] = arguments[_key5];
      }
      return logMessage(name, logLevel.WARN, ...args);
    },
    error: function () {
      for (var _len6 = arguments.length, args = new Array(_len6), _key6 = 0; _key6 < _len6; _key6++) {
        args[_key6] = arguments[_key6];
      }
      return logMessage(name, logLevel.ERROR, ...args);
    },
    fatal: function () {
      for (var _len7 = arguments.length, args = new Array(_len7), _key7 = 0; _key7 < _len7; _key7++) {
        args[_key7] = arguments[_key7];
      }
      return logMessage(name, logLevel.FATAL, ...args);
    },
    isTraceEnabled: () => logLevel.TRACE.value >= globalConfig.level.value,
    isDebugEnabled: () => logLevel.DEBUG.value >= globalConfig.level.value,
    isInfoEnabled: () => logLevel.INFO.value >= globalConfig.level.value,
    isWarnEnabled: () => logLevel.WARN.value >= globalConfig.level.value,
    isErrorEnabled: () => logLevel.ERROR.value >= globalConfig.level.value,
    isFatalEnabled: () => logLevel.FATAL.value >= globalConfig.level.value
  };
};
const logger = {
  logLevel,
  getLogger,
  configure
};
export { logger as default };
